import { useCallback, useEffect, useMemo, useState } from "react";
import { GroupMetaData } from "../../../../application/domain/group/group";
import apiClient from "../../../../application/apis/tenant-management/tenant-management-client";
import { toTotalPage } from "../../../../application/domain/pagination/paging";
import { useAuthContextV2 } from "../../../../auth-state";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";

type FetchResult = {
  groupMetaData: GroupMetaData[];
  totalPage: number;
};

export const useGroupSelect = (
  initialGroupIds: string[],
  tenantId?: string,
) => {
  const { auth } = useAuthContextV2();
  if (auth.isServiceProviderAccount() && !tenantId) {
    throw new Error("tenantId is required for service provider account");
  }
  const SEARCH_LIMIT = 50;
  const [isLoading, setIsLoading] = useState(false);
  const [initialGroups, setInitialGroups] = useState<GroupMetaData[]>([]);
  const [groups, setGroups] = useState<GroupMetaData[]>([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  const fetch = useCallback(
    async (
      params: Parameters<typeof apiClient.fetchGroupsMetadata>,
    ): Promise<FetchResult> => {
      const groupMetaData: GroupMetaData[] = [];
      const [_name, limit, _filter] = params;
      let totalPage = 1;
      setIsLoading(true);
      try {
        const response = auth.isServiceProviderAccount()
          ? await apiClient.fetchGroupsByOrganizationId(tenantId!, ...params)
          : await apiClient.fetchGroupsMetadata(...params);
        groupMetaData.push(
          ...response.data.map((g) => ({
            id: g.id,
            orgId: tenantId!,
            name: g.name,
          })),
        );
        totalPage = toTotalPage(
          Number(response.headers["pagination-count"]) ?? 1,
          limit,
        );
      } catch (e) {
        reportUnexpectedError(e);
        toast.error("グループの検索に失敗しました。");
      } finally {
        setIsLoading(false);
      }
      return {
        groupMetaData,
        totalPage,
      };
    },
    [auth, tenantId],
  );

  const searchGroups = useDebouncedCallback(async (text = "") => {
    const { groupMetaData, totalPage } = await fetch([
      page,
      SEARCH_LIMIT,
      { name: text },
    ]);
    setGroups((prev) => [...prev, ...groupMetaData]);
    setTotalPage(totalPage);
  }, 1000);

  useEffect(() => {
    searchGroups();
  }, [searchGroups, page]);

  // 初期選択されたグループがsearchGroupsの１ページ目に含まれていない可能性があるため、
  // 個別に初期選択されたグループを取得しておき、candidatesに含める
  const [isInitialGroupsFetched, setIsInitialGroupsFetched] = useState(false);
  const getInitialGroups = useCallback(async () => {
    if (isInitialGroupsFetched) return;
    if (initialGroupIds.length === 0) return;

    const { groupMetaData } = await fetch([
      1,
      initialGroupIds.length,
      { ids: initialGroupIds },
    ]);
    setInitialGroups(groupMetaData);
    setIsInitialGroupsFetched(true);
  }, [fetch, initialGroupIds, isInitialGroupsFetched]);

  useEffect(() => {
    getInitialGroups();
  }, [getInitialGroups]);

  const candidates = useMemo(() => {
    const combinedGroups = (
      initialGroups.length > 0 ? [...initialGroups, ...groups] : groups
    ).filter((group) => group.id);
    const noDuplicateGroups = combinedGroups.filter(
      (group, index, self) =>
        index === self.findIndex((t) => t.id === group.id),
    );
    return noDuplicateGroups.map((group) => ({
      value: group.id ?? "",
      label: group.name,
    }));
  }, [groups, initialGroups]);

  return {
    isLoading,
    searchGroups,
    candidates,
    page,
    setPage,
    totalPage,
  };
};
